import React from 'react';
import Typography from '@material-ui/core/Typography';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { usePermissions, useTranslation } from '@michelin/central-provider';
import { MechanicalProfileOVM } from '@michelin/fcp-view-models';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useGetMechanicalProfileList, useSaveMechanicalProfile } from 'hooks/useMechanicalProfile';
import { getBaseUrl } from 'prefs-and-service-offers';
import { useHistory, useParams } from 'react-router-dom';
import { AreaHeader, OpenFullButton, Panel, ToggleRow, WhyItMatters } from './components';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    row: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '1rem',
      padding: '0.85rem 0',
      borderBottom: `1px solid ${theme.palette.divider}`,
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    label: {
      fontSize: '0.9rem',
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    description: {
      marginTop: 2,
      fontSize: '0.8rem',
      color: theme.palette.text.secondary,
    },
    instructions: {
      fontSize: '0.85rem',
      color: theme.palette.text.secondary,
      fontStyle: 'italic',
    },
    viewLink: {
      fontSize: '0.85rem',
      fontWeight: 600,
      color: theme.palette.primary.main,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      border: 'none',
      background: 'transparent',
      padding: 0,
    },
  }),
);

function getProfileRecencyTimestamp(profile: MechanicalProfileOVM): number {
  const dateValue = profile.update_date || profile.created_date;
  if (!dateValue) return 0;
  const parsed = Date.parse(dateValue);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function normalizeSpecialInstructions(value?: string): string {
  if (!value) return '';
  const trimmed = value.trim();
  const withoutOuterParagraph = trimmed.replace(/^<p>([\s\S]*)<\/p>$/i, '$1');
  return withoutOuterParagraph
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .trim();
}

interface MechanicalSubAreaProps {
  onOpenBillingApproval?: () => void;
}

interface MechanicalAreaStatusResult {
  headerStatus: { label: string; tone: 'success' | 'danger' };
  incompleteCount: number;
  selectedProfile: MechanicalProfileOVM | null;
  profileSpecialInstructions: string;
  driversCanRequestService: boolean;
}

export function useMechanicalAreaStatus(): MechanicalAreaStatusResult {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const { selectedAccount } = useParams<{ selectedAccount: string }>();
  const fleetId = permissions.location?.customer_number || selectedAccount || '';

  const { data: mechanicalProfilesData = [] } = useGetMechanicalProfileList({ fleetId });

  const mechanicalProfiles = React.useMemo(
    () => (Array.isArray(mechanicalProfilesData) ? mechanicalProfilesData : []),
    [mechanicalProfilesData],
  );

  const selectedProfile = React.useMemo(() => {
    if (!mechanicalProfiles.length) return null;
    return mechanicalProfiles.reduce<MechanicalProfileOVM | null>((latest, profile) => {
      if (!latest) return profile;
      return getProfileRecencyTimestamp(profile) > getProfileRecencyTimestamp(latest) ? profile : latest;
    }, null);
  }, [mechanicalProfiles]);

  const driversCanRequestService =
    selectedProfile?.main?.allowed_to_call_in_requests?.some((value) => String(value).trim().toLowerCase() === 'driver') ||
    false;

  const profileSpecialInstructions = normalizeSpecialInstructions(selectedProfile?.main?.special_instructions);
  const incompleteCount = profileSpecialInstructions ? 0 : 1;
  const headerStatus =
    incompleteCount > 0
      ? { label: t('{{count}} incomplete', { count: incompleteCount }), tone: 'danger' as const }
      : { label: t('Complete'), tone: 'success' as const };

  return {
    headerStatus,
    incompleteCount,
    selectedProfile,
    profileSpecialInstructions,
    driversCanRequestService,
  };
}

export function MechanicalSubArea({ onOpenBillingApproval }: MechanicalSubAreaProps = {}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const permissions = usePermissions();
  const { selectedAccount } = useParams<{ selectedAccount: string }>();
  const fleetId = permissions.location?.customer_number || selectedAccount || '';
  const { headerStatus, profileSpecialInstructions, driversCanRequestService, selectedProfile } = useMechanicalAreaStatus();
  const saveMechanicalProfile = useSaveMechanicalProfile({ accountNumber: fleetId, profileId: selectedProfile?.id });

  const openLegacyMechanical = () => history.push(`${getBaseUrl(false)}/mechanical-profile`);
  const handleDriverRequestToggle = async (checked: boolean) => {
    if (!selectedProfile || !fleetId) return;

    const currentAllowed = selectedProfile.main?.allowed_to_call_in_requests ?? [];
    const nextAllowed = checked
      ? [...new Set([...currentAllowed, 'driver'])]
      : currentAllowed.filter((value) => String(value).trim().toLowerCase() !== 'driver');

    const updatedProfile: MechanicalProfileOVM = {
      ...selectedProfile,
      main: {
        ...selectedProfile.main,
        allowed_to_call_in_requests: nextAllowed,
      },
    };

    await saveMechanicalProfile.mutateAsync({ accountNumber: fleetId, data: updatedProfile, action: 'edit' });
    await queryClient.invalidateQueries(['get-mechanical-profile-list', fleetId]);
    enqueueSnackbar(t('Mechanical profile information saved.'), {
      variant: 'success',
      anchorOrigin: { vertical: 'top', horizontal: 'center' },
    });
  };

  return (
    <>
      <AreaHeader
        icon={<span aria-hidden>&#128295;</span>}
        iconColor="#fdf3c9"
        title={t('Mechanical Preferences')}
        subtitle={t('The ground rules for non-tire repairs - who can request work and your standing instructions.')}
        status={headerStatus}
      />

      <Panel>
        <ToggleRow
          label={t('Drivers can request mechanical service')}
          description={t('Allow drivers to initiate non-tire repairs from the road.')}
          checked={driversCanRequestService}
          onChange={handleDriverRequestToggle}
        />
        <div className={classes.row}>
          <div>
            <Typography className={classes.label}>{t('Special instructions')}</Typography>
            <Typography className={classes.instructions}>
              {profileSpecialInstructions || t('No profile-level special instructions configured.')}
            </Typography>
          </div>
        </div>
        <div className={classes.row}>
          <div>
            <Typography className={classes.label}>{t('Contact approval limits')}</Typography>
            <Typography className={classes.description}>{t('Dollar ceilings each approver can authorize.')}</Typography>
          </div>
          <button type="button" className={classes.viewLink} onClick={onOpenBillingApproval}>
            {t('View')} &rarr;
          </button>
        </div>
      </Panel>

      <WhyItMatters>
        {t(
          'Clear rules keep technicians and approvers acting consistently and within your limits on every mechanical job.',
        )}
      </WhyItMatters>

      <OpenFullButton label={t('Open full Mechanical Preferences')} onClick={openLegacyMechanical} />
    </>
  );
}

export default MechanicalSubArea;
